import { useEffect } from 'react';

// ----------------------------------------------------------------------

/**
 * 파비콘을 동적으로 변경하는 컴포넌트
 * prod가 아닌 경우 파비콘을 흑백으로 변환
 */
export function Favicon() {
  useEffect(() => {
    const isProd = import.meta.env.MODE === 'prod';
    
    if (isProd) {
      // prod인 경우 기본 파비콘 사용
      return;
    }

    // prod가 아닌 경우 파비콘을 흑백으로 변환
    const convertToGrayscale = (imageUrl: string): Promise<string> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              reject(new Error('Failed to get canvas context'));
              return;
            }
            
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            
            // 흑백 변환 (grayscale)
            for (let i = 0; i < data.length; i += 4) {
              const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
              data[i] = gray;     // R
              data[i + 1] = gray; // G
              data[i + 2] = gray; // B
            }
            
            ctx.putImageData(imageData, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } catch (error) {
            reject(error);
          }
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageUrl;
      });
    
    const updateFaviconLinks = async () => {
      try {
        // 기본 파비콘 변환
        const grayscaleFavicon = await convertToGrayscale('/favicon/favicon-32x32.png');
        
        // 모든 파비콘 링크 업데이트
        const updateLink = (selector: string, href: string) => {
          const link = document.querySelector(selector) as HTMLLinkElement;
          if (link) {
            link.href = href;
          }
        };
        
        updateLink('link[rel="icon"]', grayscaleFavicon);
        updateLink('link[rel="shortcut icon"]', grayscaleFavicon);
        updateLink('link[rel="icon"][sizes="16x16"]', grayscaleFavicon);
        updateLink('link[rel="icon"][sizes="32x32"]', grayscaleFavicon);
        updateLink('link[rel="icon"][sizes="96x96"]', grayscaleFavicon);
        
        // apple-touch-icon 변환
        try {
          const grayscaleAppleIcon = await convertToGrayscale('/favicon/apple-touch-icon.png');
          updateLink('link[rel="apple-touch-icon"]', grayscaleAppleIcon);
        } catch {
          // apple-touch-icon 변환 실패는 무시
        }
      } catch (error) {
        console.warn('Error converting favicon to grayscale:', error);
      }
    };
    
    updateFaviconLinks();
  }, []);
  
  return null;
}

